import type {
  GetGfapResponse,
  NetworkProvider,
  PostBatchesResponse,
} from '@/util/types';
import { InstallationStatus } from '@/util/types';
import type { JSONSchema7 } from 'json-schema';

export async function fetchGfap(
  home_id: string,
  activation_code: string,
): Promise<GetGfapResponse> {
  const response = await fetch(
    `/api/gfaps/${home_id}?activation_code=${activation_code}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok)
    throw new Error(
      `Failed to fetch GFAP: ${response.status} ${response.statusText}`,
    );

  return await response.json();
}

export async function fetchNetworkProviderFormSchema(): Promise<
  JSONSchema7 | undefined
> {
  const response = await fetch(`/api/network_provider/form_schema`, {
    headers: {
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  if (!response.ok)
    throw new Error(
      `Failed to fetch form schema: ${response.status} ${response.statusText}`,
    );

  return await response.json();
}

export async function fetchNetworkProvider(): Promise<NetworkProvider> {
  const response = await fetch(`/api/network_provider`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok)
    throw new Error(
      `Failed to fetch network provider: ${response.status} ${response.statusText}`,
    );

  return await response.json();
}

export async function fetchImage(
  image_id: string,
  variant: '256' | 'public' = 'public',
): Promise<{ imageUrl: string }> {
  const response = await fetch(`/api/images/${image_id}?variant=${variant}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok)
    throw new Error(
      `Failed to fetch image: ${response.status} ${response.statusText}`,
    );

  return await response.json();
}

export async function createGfap(batchData: {
  quantity: number;
}): Promise<PostBatchesResponse> {
  const response = await fetch('/api/gfaps/batches', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(batchData),
  });

  if (!response.ok)
    throw new Error(
      `Failed to create GFAP: ${response.status} ${response.statusText}`,
    );

  return await response.json();
}

export async function logoutRequest(): Promise<void> {
  const response = await fetch('/api/network_provider/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok)
    throw new Error(
      `Failed to logout: ${response.status} ${response.statusText}`,
    );
}

export async function getAuthenticated(): Promise<
  { success: false } | { success: true; exp: number }
> {
  const response = await fetch('/api/network_provider/authenticated', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) return { success: false };
  return await response.json();
}

export async function postLogin(provider_password: string) {
  const response = await fetch(`/api/network_provider/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      provider_password,
    }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.message);
  }

  return (await response.json()) as {
    token: string;
  };
}

export async function updateInstallationStatus(
  installationId: string,
  status: InstallationStatus,
): Promise<void> {
  const response = await fetch(`/api/gfaps/${installationId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      installation_status: status,
    }),
  });

  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
}

export interface AddressData {
  id?: string;
  street: string;
  city: string;
  zip: string;
  coordinates: {
    lat: number;
    lon: number;
  };
}

export interface CreateAddressResponse {
  message: string;
}

export async function createAddress(
  address: AddressData,
): Promise<CreateAddressResponse> {
  const addressWithId = {
    ...address,
    id: address.id || crypto.randomUUID(),
  };

  const response = await fetch('/api/addresses', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify([addressWithId]),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to create address: ${response.status} ${response.statusText}`,
    );
  }

  return await response.json();
}
